import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import DataGrid, {
  Toolbar,
  Item,
  Selection,
  Paging,
  Scrolling,
  LoadPanel,
  Editing,
  Column,
  GroupPanel,
  Sorting,
  SearchPanel,
  Lookup,
  KeyboardNavigation,
  Format,
} from 'devextreme-react/data-grid';
import { ConfigButton, DeleteButton, CloneButton, onRowPrepared, AddButton, onInitNewRow } from './utilsUI';
import { dataSource, updateSummary } from './utilsAPI';
import { dataGridColumns } from './dataGridColumns';
import { getParamsData } from './Params/utilsAPI';
import { useDataCadastro } from '../../../../contexts/DataCadastroContext';
import { logDebug, logInfo } from '../../../../utils/logHandler';

export const Movimentacoes = ({ grupoId, className, grupoEmissao }) => {

  const { dataCadastro } = useDataCadastro();
  const gridRef = useRef();
  const [summary, setSummary] = useState({});
  const [newRowPosition, setNewRowPosition] = useState("last");

  // Carrega os parâmetros e atualiza o resumo
  const loadParamsData = useCallback(async () => {
    try {
      const paramsData = await getParamsData();
      setNewRowPosition(paramsData.default_new_row_position);
      updateSummary(grupoId, setSummary);
    } catch (error) {
      console.error("Erro ao carregar os parâmetros:", error);
    }
  }, [grupoId]);

  useEffect(() => {
    loadParamsData();
  }, [loadParamsData]);

  // Memoriza o dataSource para evitar recriações desnecessárias
  const memoizedDataSource = useMemo(() => dataSource(grupoId, setSummary), [grupoId, setSummary]);

  const handleEditorPreparing = useCallback((e) => {
    e.editorOptions.dropDownOptions = { width: 400 };
  }, []);

  const handleInitNewRow = useCallback(
    (row) => {
      onInitNewRow(row, grupoId);
    },
    [grupoId]
  );

  const handleOnSaved = useCallback(() => {
    updateSummary(grupoId, setSummary);
  }, [grupoId, setSummary]);

  // Usa useMemo para gerar as colunas somente quando dataCadastro for carregado
  const columns = useMemo(
    () => (dataCadastro ? dataGridColumns(dataCadastro) : []),
    [dataCadastro]
  );

  // Se os dados de cadastro ainda não foram carregados, exibe um indicador
  if (!dataCadastro) {
    return <div>Carregando dados...</div>;
  }

  return (
    <div className={`row mx-0 ${className}`}>
      <DataGrid
        ref={gridRef}
        id={`gridMovimentacao${grupoId}`}
        dataSource={memoizedDataSource}
        showColumnLines
        showRowLines
        showBorders
        errorRowEnabled={false}
        rowAlternationEnabled={false}
        allowColumnReordering={false}
        allowColumnResizing
        onEditorPreparing={handleEditorPreparing}
        hoverStateEnabled
        onRowPrepared={onRowPrepared}
        onInitNewRow={handleInitNewRow}
        // autoNavigateToFocusedRow
        activeStateEnabled
        onSaved={handleOnSaved}
        repaintChangesOnly
      >
        <Toolbar>
          <Item location="before" text="Movimentações" />
          <Item location="after">
            <AddButton
              gridRef={gridRef}
              dataSource={dataSource}
              grupoId={grupoId}
              updateSummary={updateSummary}
              setSummary={setSummary}
              grupoEmissao={grupoEmissao}
            />
          </Item>
          <Item location="after" name="saveButton" options={{ stylingMode: "contained" }} />
          <Item location="after">
            <div className="vr mx-2" />
          </Item>
          <Item location="after">
            <CloneButton
              gridRef={gridRef}
              dataSource={dataSource}
              grupoId={grupoId}
              updateSummary={updateSummary}
              setSummary={setSummary}
            />
          </Item>
          <Item location="after">
            <DeleteButton
              gridRef={gridRef}
              dataSource={dataSource}
              grupoId={grupoId}
              updateSummary={updateSummary}
              setSummary={setSummary}
            />
          </Item>
          <Item location="after" name="revertButton" options={{ stylingMode: "contained" }} />
          {/*
          <Item location="after">
            <ConfigButton setPopupParamsVisible={setPopupParamsVisible} />
          </Item>
          */}
        </Toolbar>

        <Selection mode="multiple" showCheckBoxesMode="always" />

        <Editing
          mode="batch"
          allowUpdating
          allowAdding
          allowDeleting={false}
          refreshMode="repaint"
          selectTextOnEditStart={false}
          startEditAction="click"
          useIcons
          newRowPosition={newRowPosition}
        />

        <KeyboardNavigation editOnKeyPress />
        <Paging enabled={false} />
        <Scrolling mode="standard" useNative />
        <LoadPanel enabled />
        <GroupPanel visible={false} />
        <SearchPanel visible={false} />
        <Sorting mode="none" />

        {columns.map((item, index) => (
          <Column
            key={index}
            caption={item.caption}
            dataField={item.dataField}
            dataType={item.dataType}
            width={item.width}
            minWidth={item.minWidth}
            allowEditing={item.allowEditing}
            alignment={item.alignment}
            defaultSortOrder={item.defaultSortOrder}
            fixed={item.fixed}
            fixedPosition={item.fixedPosition}
            sortOrder={item.sortOrder}
            calculateCellValue={item.calculateCellValue}
            calculateDisplayValue={item.calculateDisplayValue}
          >
            {item.lookup && (
              <Lookup
                dataSource={{
                  store: { data: item.dataSource, type: 'array', key: 'id' },
                  paginate: true,
                  pageSize: 5,
                }}
                valueExpr={item.valueExpr}
                displayExpr={item.displayExpr}
                allowClearing={item.allowClearing}
              />
            )}
            {item.formatType && <Format type={item.formatType} precision={item.formatPrecision} />}
          </Column>
        ))}

        <Column type="selection" width={55} fixed fixedPosition="right" />
      </DataGrid>

      <div className="d-flex mx-0 p-0 fw-bold rounded-bottom pt-2 pb-2">
        <div className="px-2">Items: {summary.total_item ?? 0}</div>
        <div className="px-2 text-danger">Débitos: {summary.total_debito ?? '0,00'}</div>
        <div className="px-2 text-success">Créditos: {summary.total_credito ?? '0,00'}</div>
        <div className="px-2 text-primary">Saldo: {summary.saldo ?? '0,00'}</div>
      </div>
    </div>
  );
};
